import { Box, Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { fetchCustomerOrgById } from "../../../customer/customerOrganizationSlice";
import { fetchWorkLocationsAndContractsByOrgId } from "../../../customer/customerWorkLocationsSlice";
import { AdditionalInfoFormContainer } from "../../additionalInfo/styles";
import { WorkLocation } from "../../orderTypes";
import { BasicDetails } from "./formSections/BasicDetails";
import { Buttons } from "./formSections/Buttons";
import { CustomerCostCenters } from "./formSections/CustomerCostCenters";
import { Description } from "./formSections/Description";
import { ExtraInfo } from "./formSections/ExtraInfo";
import { Requirements } from "./formSections/Requirements";
import { Skills } from "./formSections/Skills";
import { Terms } from "./formSections/Terms";
import { WorkDetails } from "./formSections/WorkDetails";
import LongOrderWorkAgreementPreview from "./LongOrderWorkAgreementPreview/LongOrderWorkAgreementPreview";

type Props = {
  saveAction: () => void;
  addNewAction?: () => void;
  cancelAction: () => void;
};

export const LongOrderForm = ({
  saveAction,
  addNewAction,
  cancelAction,
}: Props) => {
  const dispatch = useAppDispatch();

  const { workTitle } = useAppSelector((state) => state.localized);
  const { watch } = useFormContext();
  const { customerOrgId, orderTemplate, workLocation, edit } = watch();

  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [ccOptions, setCcOptions] = useState<AutocompleteType[]>([]);
  const [orgCCs, setOrgCCs] = useState<AutocompleteType[]>([]);
  const [customerOrg, setCustomerOrg] = useState<{ id: string; name: string }>({
    id: "",
    name: "",
  });
  const [customerCompany, setCustomerCompany] = useState<{
    id: string;
    name: string;
  }>({
    id: "",
    name: "",
  });

  useEffect(() => {
    if (!customerOrgId) return;
    dispatch(fetchWorkLocationsAndContractsByOrgId({ id: customerOrgId })).then(
      (action) => {
        if (isFulfilled(action)) {
          const workLocations = action.payload.workLocations;
          setWorkLocations(workLocations as WorkLocation[]);
        }
      },
    );
    dispatch(fetchCustomerOrgById({ id: customerOrgId })).then((action) => {
      if (isFulfilled(action)) {
        setOrgCCs(action.payload.costCenters.filter((cc) => cc.active));
        setCustomerOrg({ id: action.payload.id, name: action.payload.name });
        setCustomerCompany({
          id: action.payload.customerCompanyId,
          name: action.payload.customerCompanyName || "",
        });
      }
    });
  }, [customerOrgId, dispatch]);

  const selectWorkLocation = useCallback(
    (id: string) => {
      const workLocation = workLocations.find((wl) => wl.id === id);
      if (workLocation) {
        const workLocationCcs = workLocation.customerCCs.filter(
          (cc) => cc.active,
        );
        const ccs = workLocationCcs.length ? workLocationCcs : orgCCs;
        setCcOptions(ccs);
      } else {
        setCcOptions([]);
      }
    },
    [workLocations, orgCCs],
  );

  // Set cc options for edit view
  useEffect(() => {
    if (!workLocation) return;
    const location = workLocations.find((wl) => wl.id === workLocation.id);
    const workLocationCCs =
      location?.customerCCs?.filter((cc) => cc.active) || [];
    const ccs = location?.customerCCs.length ? workLocationCCs : orgCCs;
    setCcOptions(ccs);
  }, [workLocations, workLocation, orgCCs]);

  return (
    <>
      {!edit && (
        <Box
          paddingTop={2}
          paddingLeft={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Typography
            variant="header"
            fontSize={24}
            fontWeight={600}
            paddingRight={2}
          >
            {orderTemplate?.workTitleId && workTitle[orderTemplate.workTitleId]}
          </Typography>
          <Typography variant="secondary">{workLocation?.name}</Typography>
        </Box>
      )}
      <Grid container>
        <Grid item xs={!edit ? 8 : 12}>
          <AdditionalInfoFormContainer>
            <BasicDetails
              updateCostCenters={selectWorkLocation}
              customerOrg={customerOrg}
              customerCompany={customerCompany}
            />
            <CustomerCostCenters ccOptions={ccOptions} />
            <Terms />
            <WorkDetails />
            <Requirements />
            <Skills />
            <Description />
            <ExtraInfo />
            <Buttons saveAction={saveAction} cancelAction={cancelAction} />
          </AdditionalInfoFormContainer>
        </Grid>
        {!edit && (
          <Grid item xs={4}>
            <LongOrderWorkAgreementPreview />
          </Grid>
        )}
      </Grid>
    </>
  );
};
